import { getInspectionConfig } from "@app/models/inspectionConfig";
import { redirect } from "react-router-dom";
import getDisplayFields from "@app/lib/hooks/getDisplayFields";
import getYupSchema from "@app/lib/hooks/getYupSchema";
import Config from "@app/constants/Config";

export const nextStep = async (step, inspectionId) => {
	const {
		forms: { steps },
	} = await getInspectionConfig();

	if (step === "new") {
		// proceed to next step
		return `${Config.BASE_PATH}/inspection/${inspectionId}/1`;
	} else if (step + 1 <= steps.length) {
		// proceed to next step
		return `${Config.BASE_PATH}/inspection/${inspectionId}/${step + 1}`;
	} else if (step + 1 === steps.length + 1) {
		// the steps are done, go to summary
		return `${Config.BASE_PATH}/inspection/${inspectionId}/summary`;
	} else if (step === "summary") {
		// summary is checked, go to confirmation
		return `${Config.BASE_PATH}/inspection/${inspectionId}/confirmation`;
	} else if (step === "confirmation") {
		// confirmation is checked, go to thankyou
		return `${Config.BASE_PATH}/inspection/${inspectionId}/thankyou`;
	} else {
		return undefined;
	}
};

export const previousStep = async (step, inspectionId) => {
	const {
		forms: { steps },
	} = await getInspectionConfig();

	if (step === "new" || step === undefined) {
		return undefined;
	} else if (step > 1) {
		// proceed to previous step
		return `${Config.BASE_PATH}/inspection/${inspectionId}/${step - 1}`;
	} else if (step === "summary") {
		return `${Config.BASE_PATH}/inspection/${inspectionId}/${steps?.length}`;
	} else if (step === "confirmation") {
		return `${Config.BASE_PATH}/inspection/${inspectionId}/summary`;
	} else if (step === "thankyou") {
		return `${Config.BASE_PATH}/inspection/${inspectionId}/confirmation`;
	} else {
		return undefined;
	}
};

export const needsNextStepValidation = (step, target) => {
	if (step === "summary" || step === "confirmation" || step === "thankyou") {
		return false;
	} else {
		const targetStep = target.substring(target.lastIndexOf("/") + 1);
		const targetStepInt = parseInt(targetStep, 10);
		if (
			(Number.isInteger(targetStepInt) && targetStepInt > step) ||
			targetStep === "summary" ||
			targetStep === "confirmation" ||
			targetStep === "thankyou"
		) {
			return true;
		} else {
			return false;
		}
	}
};

export const previousStepRedirect = async (step, inspectionId) => {
	const redirectPath = await previousStep(step, inspectionId);

	if (redirectPath) {
		// proceed to next step
		return redirect(redirectPath);
	} else {
		throw new Response("", {
			status: 500,
			statusText: `Previous step for step ${step} is not defined`,
		});
	}
};

export const nextStepRedirectPath = async (step, inspectionId) => await nextStep(step, inspectionId);

export const nextStepRedirect = async (step, inspectionId) => {
	const redirectPath = await nextStepRedirectPath(step, inspectionId);

	if (redirectPath) {
		// proceed to next step
		return redirect(redirectPath);
	} else {
		throw new Response("", {
			status: 500,
			statusText: `Next step for step ${step} is not defined`,
		});
	}
};

export const getWizardSteps = (step, inspectionConfig) => {
	let current;
	let previous;
	const stepIndex = parseInt(step, 10);
	const steps = inspectionConfig?.forms?.steps;
	if (step === "new" || step === "new-follow-up" || step === undefined) {
		previous = null;
		current = 1;
	} else if (Number.isInteger(stepIndex)) {
		previous = stepIndex > 1 ? stepIndex - 1 : null;
		current = stepIndex;
	} else if (step === "summary") {
		previous = steps?.length;
		current = steps?.length + 1;
	} else if (step === "confirmation") {
		previous = "summary";
		current = steps?.length + 2;
	} else if (step === "thankyou") {
		previous = "confirmation";
		current = steps?.length + 3;
	} else {
		return null;
	}

	return {
		total: steps?.length + 3,
		current,
		previous,
	};
};

export const getFormStepErrors = (step, values, instantValidation = false) => {
	const displayFields = [...step.fieldsets];
	let rawFields = displayFields.map(({ elements = [] }) => elements).reduce((res, fields) => [...res, ...fields], []);
	rawFields = getDisplayFields(rawFields, values);
	return validateFields(rawFields, values, instantValidation);
};

export const validateFields = (fields, values, instantValidation = false) => {
	const validationSchema = getYupSchema(fields, instantValidation);
	let errors = [];
	try {
		validationSchema.validateSync(values, { abortEarly: false });
	} catch (e) {
		errors = e.inner.map(({ path, type, value, message }) => ({ path, type, value, message }));
	}
	return errors;
};

export function getJobByLocation(values, office) {
	let jobLocationID = values.kundenundeinsatzort_einsatzort_adressetyp;
	if (
		values.kundenundeinsatzort_einsatzort_adressetyp === "Siehe Anschrift" ||
		values.kundenundeinsatzort_einsatzort_adressetyp === "Einsatzwechseltätigkeit (Montage)"
	) {
		jobLocationID = `${values.kundenundeinsatzort_kunde_strasseundhausnummer}, ${values.kundenundeinsatzort_kunde_plz}, ${values.kundenundeinsatzort_kunde_ort}`;
	}
	const customerId = values.kundenundeinsatzort_kunde_kunde__ID;
	const customer = office?.kundenliste.find(({ KUNDENNR }) => {
		return KUNDENNR.toString() === customerId?.toString();
	});
	let jobLocation = customer?.EinsatzAdressen?.find(({ Adresse }) => Adresse === jobLocationID);
	if (!jobLocation) {
		return null;
	}

	const jobId = values.taetigkeit_beschreibung_ueberlassungals__ID;
	const job = jobLocation.Berufe?.find(({ Berufschluessel }) => Berufschluessel === jobId);
	return job || null;
}
